<script lang="ts" setup>
import type { FilterRelationalQuantifier } from '#layers/data-model';
import type { PrimitiveFieldPath } from '#layers/data-model/utils/collection-field-path';
import type { FilterPathSegment } from './utils/filter-path';
import { computed } from 'vue';
import { FILTER_RELATIONAL_QUANTIFIERS } from '#layers/data-model';
import { useDisplayNames } from '~/composables/use-display-names';
import { isRelationalSegment, isToManySegment } from './utils/filter-path';
import { injectFilterSchemaContext } from './utils/filter-schema-context';

const props = withDefaults(defineProps<{
	segments: FilterPathSegment[];
	includeRelationFields?: boolean;
	disabled?: boolean;
}>(), {
	includeRelationFields: true,
});

const emit = defineEmits<{
	updateFromSegment: [index: number, path: PrimitiveFieldPath];
	updateQuantifier: [index: number, quantifier: FilterRelationalQuantifier];
}>();

const quantifierOptions = FILTER_RELATIONAL_QUANTIFIERS.map((q) => ({
	label: q.replace('_', ''),
	value: q,
}));

const { findCollectionById, findFieldById, findOppositeCollectionByRelationFieldId } = injectFilterSchemaContext();
const { getFieldDisplayName } = useDisplayNames();
const includeSelectableRelationFields = computed(() => props.includeRelationFields !== false);

function getFieldName(segment: FilterPathSegment): string {
	const field = findFieldById(segment.fieldId);
	return field ? getFieldDisplayName(field) : segment.fieldId;
}

function getRootCollection() {
	const firstSegment = props.segments[0];
	const firstField = firstSegment ? findFieldById(firstSegment.fieldId) : undefined;

	return firstField?.collectionId ? findCollectionById(firstField.collectionId) : undefined;
}

function getSegmentSelectorCollection(index: number) {
	let collection = getRootCollection();

	if (!collection) return;

	for (const segment of props.segments.slice(0, index)) {
		if (!isRelationalSegment(segment)) return;

		const oppositeCollection = findOppositeCollectionByRelationFieldId(segment.fieldId);

		if (!oppositeCollection) return;

		collection = oppositeCollection;
	}

	return collection;
}

const segmentSelectorCollections = computed(() => props.segments.map((_, index) => getSegmentSelectorCollection(index)));

function onQuantifierChange(index: number, value: FilterRelationalQuantifier) {
	emit('updateQuantifier', index, value);
}

function updateFromSegment(index: number, path: PrimitiveFieldPath) {
	emit('updateFromSegment', index, path);
}
</script>

<template>
	<div class="flex flex-wrap items-center">
		<template v-for="(segment, index) in segments" :key="index">
			<!-- Separator arrow -->
			<DIcon
				v-if="index > 0"
				name="lucide:chevron-right"
				class="size-3 shrink-0 text-dimmed"
			/>

			<CollectionFieldPathSelect
				v-if="segmentSelectorCollections[index]"
				:collection="segmentSelectorCollections[index]"
				include-constrained-fields
				:include-relation-fields="includeSelectableRelationFields"
				:include-list-relation-fields="includeSelectableRelationFields"
				order="schema"
				:resolve-relation-collection="findOppositeCollectionByRelationFieldId"
				:search-placeholder="$t('filters.filter_search_placeholder')"
				@select="updateFromSegment(index, $event)"
			>
				<DButton
					color="neutral"
					variant="ghost"
					size="sm"
					class="rounded-none"
					:disabled
				>
					{{ getFieldName(segment) }}
				</DButton>
			</CollectionFieldPathSelect>

			<template v-if="isToManySegment(segment)">
				<DIcon name="lucide:chevron-right" class="size-3 shrink-0 text-dimmed" />
				<DSelect
					:model-value="segment.quantifier"
					:items="quantifierOptions"
					value-key="value"
					variant="ghost"
					trailing-icon=""
					class="rounded-none"
					:ui="{
						base: 'px-1',
						content: 'min-w-fit',
						value: 'text-xs text-primary',
					}"
					:disabled
					@update:model-value="(v: FilterRelationalQuantifier) => onQuantifierChange(index, v)"
				/>
			</template>
		</template>
	</div>
</template>
